import React, { useEffect, useState, useCallback } from 'react';
import { FiBell } from 'react-icons/fi';
import { communicationsAPI, sessionAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await communicationsAPI.getNotifications();
      const data = resp?.data?.data || resp?.data || {};
      const list = Array.isArray(data.notifications) ? data.notifications : (Array.isArray(data) ? data : []);
      setItems(list);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as a parent to view Notifications.');
      else if (status === 403) setError('Your account is not authorized to view Notifications.');
      else setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canSend = user?.role === 'admin' || user?.role === 'teacher';

  const send = async (e) => {
    e.preventDefault();
    try {
      setSending(true);
      await sessionAPI.addAnnouncement(sessionId.trim(), { title: title.trim(), message: message.trim(), isUrgent });
      showSuccess('Notification sent');
      setTitle('');
      setMessage('');
      setIsUrgent(false);
      load();
    } catch (e) {
      showError('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">Updates and alerts.</p>
        </div>

        {error && user?.role === 'parent' && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            {error}
          </div>
        )}

        {canSend && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
            <h2 className="text-sm font-medium text-slate-900 mb-4">Send notification</h2>
            <form onSubmit={send} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Session ID</label>
                  <input value={sessionId} onChange={(e) => setSessionId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="Enter session ID" />
                </div>
                <div className="flex items-center gap-2 sm:pt-7">
                  <input type="checkbox" id="urgent" checked={isUrgent} onChange={(e) => setIsUrgent(e.target.checked)} className="rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                  <label htmlFor="urgent" className="text-sm font-medium text-slate-700">Urgent</label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="Notification title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" placeholder="Write your message" />
              </div>
              <div>
                <button type="submit" disabled={sending || !sessionId.trim() || !title.trim() || !message.trim()} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:pointer-events-none">
                  {sending ? 'Sending…' : 'Send Notification'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiBell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No notifications yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n, i) => (
                <li key={i} className="p-4 flex items-start gap-3 hover:bg-slate-50/50 transition-colors">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                    <FiBell className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{n.title || n.subject || 'Notification'}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{n.message || n.body || n.preview || ''}</div>
                    <div className="text-xs text-slate-400 mt-1">{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
